import { useSuspenseQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import type { AxiosError } from "axios";
import { getContentItems20 } from "#/_generated/umbraco/delivery-api/features/content/content.ts";
import type {
	IApiContentResponseModel,
	IApiMediaWithCropsModel,
	LinkListUmbBlockElementModel,
	LinkListUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { tryGetButtonVariant } from "#/_packages/umbrella/features/button/utils";
import {
	LinkList,
	type LinkListItem,
	type LinkListOrientation,
	type LinkListVariant,
} from "#/_packages/umbrella/features/link-list/components/link-list";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import type { CmsLinkModel } from "#/_packages/umbrella/integrations/umbraco/types/cms-link";
import { convertToCroppedFocalPointUrl } from "#/_packages/umbrella/integrations/umbraco/utils/image-utils.ts";
import { getUmbLink } from "#/_packages/umbrella/integrations/umbraco/utils/link-utils.ts";
import { type Intent, intents } from "#/_packages/umbrella/types";
import {
	tryGetAnimationScrollType,
	tryGetHeadingLevel,
	tryGetReadWidthLength,
	tryGetVariant,
} from "#/_packages/umbrella/utils";
import {
	getAltText,
	getAltTextFromString,
} from "#/_packages/umbrella/utils/image-utils.ts";

type LinkListBlockProps = BlockItemProps & {
	content: LinkListUmbBlockElementModel;
	settings: LinkListUmbBlockSettingsElementModel;
};

type LinkListLinkedContentMetadata = {
	title?: string;
	intro?: string;
	teaser?: LinkListItem["teaser"];
};

type ContentWithSharingProperties = IApiContentResponseModel & {
	properties?: {
		shareDescription?: string | null;
		shareImage?: IApiMediaWithCropsModel[] | null;
	};
};

const linkListVariants = ["buttons", "simple", "submenu", "teasers"] as const;

function tryGetLinkListVariant(value?: string | null): LinkListVariant {
	return linkListVariants.includes(value as LinkListVariant)
		? (value as LinkListVariant)
		: "simple";
}

function tryGetIntentOrNone(value?: string | null): Intent {
	return intents.includes(value as Intent) ? (value as Intent) : "none";
}

export function getLinkHref(link: CmsLinkModel) {
	return getUmbLink(link)?.href ?? "#";
}

export function toLinkItem(
	link: CmsLinkModel,
	metadata?: LinkListLinkedContentMetadata,
): LinkListItem | undefined {
	const normalizedLink = getUmbLink(link, { id: link.destinationId });

	if (!normalizedLink) return;

	return {
		...normalizedLink,
		label: metadata?.title ?? normalizedLink.label,
		intro: metadata?.intro,
		teaser: metadata?.teaser,
	};
}

function getLinkedContentIds(links: readonly CmsLinkModel[]) {
	return Array.from(
		new Set(
			links
				.map((link) => link.destinationId)
				.filter((id): id is string => !!id),
		),
	);
}

function toLinkedContentMetadata(
	content: IApiContentResponseModel,
): LinkListLinkedContentMetadata {
	const contentWithSharing = content as ContentWithSharingProperties;
	const shareImage = contentWithSharing.properties?.shareImage?.[0];
	const title = content.name ?? undefined;
	const intro = contentWithSharing.properties?.shareDescription ?? undefined;

	return {
		title,
		intro,
		teaser: {
			imageSrc: shareImage
				? convertToCroppedFocalPointUrl(shareImage, { width: 768 })
				: undefined,
			imageAlt: shareImage
				? getAltText(shareImage) || getAltTextFromString(title)
				: undefined,
			shareDescription: intro,
		},
	};
}

async function getLinkedContentMetadata(
	ids: readonly string[],
	shouldFetch: boolean,
): Promise<Record<string, LinkListLinkedContentMetadata>> {
	if (!shouldFetch || ids.length === 0) {
		return {};
	}

	try {
		const response = await getContentItems20(
			{
				id: [...ids],
			},
			{ paramsSerializer: { indexes: null } },
		);

		if (response.status !== 200) {
			return {};
		}
		return response.data.reduce<Record<string, LinkListLinkedContentMetadata>>(
			(result, item) => {
				result[item.id] = toLinkedContentMetadata(item);
				return result;
			},
			{},
		);
	} catch (e) {
		const error = e as AxiosError;
		console.log(error);
		return {};
	}
}

export default function LinkListUmbBlock({
	className,
	content,
	settings,
}: LinkListBlockProps) {
	const listType = tryGetLinkListVariant(content.properties?.type);
	const links = content.properties?.links ?? [];
	const linkedContentIds = getLinkedContentIds(links);
	const currentPath = useRouterState({
		select: (state) => state.location.pathname,
	});
	const shouldFetchLinkedContent =
		listType === "submenu" || listType === "teasers";

	const { data: linkedContentMetadata } = useSuspenseQuery({
		queryKey: [
			"link-list-linked-content",
			content.id,
			listType,
			linkedContentIds.join(","),
		],
		queryFn: () =>
			getLinkedContentMetadata(linkedContentIds, shouldFetchLinkedContent),
	});
	const items = links.map((link) =>
		toLinkItem(
			link,
			link.destinationId
				? linkedContentMetadata[link.destinationId]
				: undefined,
		),
	);

	return (
		<LinkList
			id={settings.properties?.generatedId ?? content.id}
			className={className}
			links={items}
			type={listType}
			hideHeading={content.properties?.hideHeading ?? false}
			headingLevel={tryGetHeadingLevel(content.properties?.headingLevel, 5)}
			headingAppearance={content.properties?.headingAppearance}
			hideIntro={content.properties?.hideIntro ?? false}
			intentColor={tryGetIntentOrNone(content.properties?.intentColor)}
			buttonVariant={tryGetButtonVariant(content.properties?.typeVariant)}
			teaserVariant={tryGetVariant(content.properties?.typeVariant)}
			orientation={
				(content.properties?.orientation as LinkListOrientation) ??
				"orientation-vertical"
			}
			readWidthToggle={settings.properties?.readWidthToggle ?? false}
			readWidthLength={tryGetReadWidthLength(
				settings.properties?.readWidthLengthPicker,
			)}
			animationScrollType={tryGetAnimationScrollType(
				settings.properties?.animationScrollType,
			)}
			animationHoverType={settings.properties?.animationHoverType ?? []}
			applyAnimationToChildren={
				settings.properties?.animationApplyToChildren ?? false
			}
			staggerAnimation={settings.properties?.animationStagger ?? false}
			currentPath={currentPath}
		/>
	);
}
